// shared/products/performance-it/roles.ts
import type { SystemRoleDefinition } from "../../roles";

/**
 * The skyPerformance IT role catalog (spec 2026-10-26 §4.4). Three roles, immutable,
 * instance-scoped, `product_key = 'skyperformance_it'`, seeded by migration 0032 and refreshed by
 * the boot seed like every SYSTEM_ROLES entry. A separate product: holding a hire or skyPerformance
 * role grants nothing here, and these roles grant nothing there. Nothing derives `pit_hr`; HR is
 * granted explicitly.
 *
 * `name` is a frozen join key (`public.roles.name`); labels may change, names never.
 */
export const PERFORMANCE_IT_ROLE_NAMES = {
  employee: "pit_employee",
  manager: "pit_manager",
  hr: "pit_hr",
} as const;

export type PerformanceItRoleName =
  (typeof PERFORMANCE_IT_ROLE_NAMES)[keyof typeof PERFORMANCE_IT_ROLE_NAMES];

/** Lower = more privileged, compared only against other IT roles. */
export const PERFORMANCE_IT_ROLE_HIERARCHY: Record<PerformanceItRoleName, number> = {
  pit_hr: 1,
  pit_manager: 2,
  pit_employee: 3,
};

/**
 * Phase 0 permissions: the bootstrap read (every role) and the settings read (HR). Every key is
 * `performance_it.*`, never a hire or a skyPerformance (`performance.*`) key.
 */
export const PERFORMANCE_IT_PERMISSION_KEYS = [
  "performance_it.bootstrap.read",
  "performance_it.settings.read",
] as const;

type PermissionMap = Record<(typeof PERFORMANCE_IT_PERMISSION_KEYS)[number], boolean>;

/** Employee and manager: the bootstrap read only. */
const MEMBER_PERMISSIONS: PermissionMap = {
  "performance_it.bootstrap.read": true,
  "performance_it.settings.read": false,
};

/** HR: each key spelled out, so a key added later is granted to HR deliberately, not by default. */
export const PIT_HR_PERMISSIONS: PermissionMap = {
  "performance_it.bootstrap.read": true,
  "performance_it.settings.read": true,
};

export const PERFORMANCE_IT_ROLES: SystemRoleDefinition[] = [
  {
    name: PERFORMANCE_IT_ROLE_NAMES.employee,
    label: "Employee",
    description: "Takes part in performance reviews: their own self-assessment and results.",
    category: "Staff",
    scope: "instance",
    product: "skyperformance_it",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: true,
    permissions: { ...MEMBER_PERMISSIONS },
    // No navigation flags yet: the IT shell draws its own nav (spec §4.6).
    navigation: {},
  },
  {
    name: PERFORMANCE_IT_ROLE_NAMES.manager,
    label: "Manager",
    description: "Reviews the people who report to them.",
    category: "Manager",
    scope: "instance",
    product: "skyperformance_it",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: true,
    permissions: { ...MEMBER_PERMISSIONS },
    navigation: {},
  },
  {
    name: PERFORMANCE_IT_ROLE_NAMES.hr,
    label: "HR",
    description:
      "The skyPerformance IT administrator: runs review cycles and configures the product.",
    category: "Admin",
    scope: "instance",
    product: "skyperformance_it",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: true,
    permissions: { ...PIT_HR_PERMISSIONS },
    navigation: {},
  },
];
